import React, { useState, useRef, useEffect } from 'react';
import {
  Camera,
  Upload,
  X,
  Check,
  Package,
  Loader2,
  AlertCircle,
  ScanLine,
  FileText,
  Link2,
  ChevronRight,
  RefreshCw,
  Trash2,
  Search,
  CheckCircle,
  Clock,
  Truck,
  Home,
  Aperture,
  Smartphone,
  Mail,
  ShieldAlert,
  Building2
} from 'lucide-react';
import { supabase, createManufacturer, fetchManufacturers } from '../supabaseClient';
import { ITEM_ARRIVED_STATUSES, RECEIPT_TERMINAL_STATUSES } from '../utils/statusConstants';

// Phases shown during AI scan — mirrors the real pipeline, with durations (seconds)
// calibrated from production logs. The final phase has no duration: it holds until
// the scan completes, which matches reality (ink verification is the longest stage).
const PROCESSING_PHASES = [
  { label: 'Enhancing & reading document', sub: 'Upscaling image, OCR mapping every word', duration: 4 },
  { label: 'AI extracting line items', sub: 'Parsing supplier, SKUs, quantities, totals', duration: 15 },
  { label: 'Deep re-read pass', sub: 'Second AI model verifying every SKU character', duration: 17 },
  { label: 'Matching against catalog', sub: 'Cross-checking SKUs with known products', duration: 3 },
  { label: 'AI ink verification', sub: 'Zooming into the ink to confirm uncertain SKUs', duration: null },
];

const InvoiceScanner = ({ storeId, onClose, onInvoiceProcessed, onOrderSkus = [] }) => {
  const [step, setStep] = useState('capture'); // capture, processing, review, correlate
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedItems, setExtractedItems] = useState([]);
  const [invoiceInfo, setInvoiceInfo] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [correlations, setCorrelations] = useState({});
  const [receipts, setReceipts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showReceiptPicker, setShowReceiptPicker] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [overrideManufacturerId, setOverrideManufacturerId] = useState(null); // null = auto-resolve from supplier name
  const [showNewMfrInput, setShowNewMfrInput] = useState(false);
  const [newMfrName, setNewMfrName] = useState('');
  const [isCreatingMfr, setIsCreatingMfr] = useState(false);

  // On-order prior sent to the scanner: the SKUs this store is actively
  // waiting on from any manufacturer. The backend uses this to prime the
  // SKU re-read (an arriving invoice almost always fulfills open orders)
  // and to validate/auto-correct readings. We pass brand alongside each
  // SKU so the backend can narrow to the detected supplier when possible.
  const expectedSkusPayload = (onOrderSkus || [])
    .map((o) => ({
      sku: (o.sku || '').trim(),
      name: (o.name || o.description || '').trim(),
      brand: (o.brand || o.manufacturerName || '').trim(),
    }))
    .filter((o) => o.sku);
  const [scannedPages, setScannedPages] = useState([]); // Track scanned pages: [{ pageNumber, imagePreview, itemCount }]
  const [queueProgress, setQueueProgress] = useState(null); // { current: 2, total: 4 } during batch scanning
  const [processingPhase, setProcessingPhase] = useState(0); // Index into PROCESSING_PHASES while scanning
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Detect problematic/unresolved SKUs that need human attention
  const isProblematicSku = (sku) => {
    if (!sku || sku.trim() === '') return true;
    const upper = sku.toUpperCase().replace(/[\s_-]/g, '');
    
    // 1. Catch known placeholder words (any combo the AI might invent)
    const placeholderWords = ['NOTFOUND', 'UNKNOWN', 'UNABLE', 'DETERMINE', 'UNREADABLE', 'MISSING', 'ERROR', 'ILLEGIBLE', 'UNCLEAR'];
    if (placeholderWords.some(word => upper.includes(word))) return true;
    
    // 2. Exact short placeholders
    if (['N/A', 'NA', 'TBD', '???', 'NONE'].includes(sku.trim().toUpperCase())) return true;
    
    // 3. Real furniture SKUs always contain at least one digit.
    //    All-letter strings longer than 5 chars (like UNABLE_TO_DET) are placeholders, not SKUs.
    const stripped = sku.replace(/[\s_\-\/]/g, '');
    const hasDigit = /\d/.test(stripped);
    if (!hasDigit && stripped.length > 5) return true;
    
    return false;
  };

  // Count how many items have problematic SKUs
  const problemItemCount = extractedItems.filter(item => isProblematicSku(item.sku)).length;
  
  // Detect if user is on mobile device
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()) || (isTouchDevice && isSmallScreen));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Advance the AI processing phase indicator while a scan is running.
  // Phases advance on a schedule calibrated to real pipeline timing, then hold
  // on the final phase (ink verification) until the backend responds.
  useEffect(() => {
    if (step !== 'processing') {
      setProcessingPhase(0);
      return;
    }
    setProcessingPhase(0); // restart phases for each new page in a batch
    const start = Date.now();
    // Cumulative thresholds (ms) at which each phase ends
    const thresholds = [];
    let acc = 0;
    for (const phase of PROCESSING_PHASES) {
      if (phase.duration == null) break;
      acc += phase.duration * 1000;
      thresholds.push(acc);
    }
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      let idx = thresholds.findIndex(t => elapsed < t);
      if (idx === -1) idx = PROCESSING_PHASES.length - 1;
      setProcessingPhase(idx);
    }, 500);
    return () => clearInterval(id);
  }, [step, queueProgress?.current]);

  // Load receipts for correlation
  useEffect(() => {
    loadReceipts();
  }, []);

  // Load manufacturers for override dropdown
  useEffect(() => {
    if (storeId) {
      fetchManufacturers(storeId).then(data => {
        if (data) setManufacturers(data);
      });
    }
  }, [storeId]);

  // Handle video element setup when stream changes
  useEffect(() => {
    if (cameraStream && videoRef.current) {
      const video = videoRef.current;
      
      // Reset video ready state
      setVideoReady(false);
      
      // Set the stream
      video.srcObject = cameraStream;
      
      // iOS needs these handlers
      video.onloadedmetadata = () => {
        console.log('Video metadata loaded');
        video.play()
          .then(() => {
            console.log('Video playing');
            setVideoReady(true);
          })
          .catch(err => {
            console.error('Video play error:', err);
            setCameraError('Could not start video playback');
          });
      };
      
      video.oncanplay = () => {
        console.log('Video can play');
      };
      
      video.onerror = (e) => {
        console.error('Video error:', e);
        setCameraError('Video error occurred');
      };
    }
  }, [cameraStream]);

  const loadReceipts = async () => {
    try {
      let query = supabase
        .from('receipts')
        .select(`
          id,
          customer_name,
          customer_phone,
          delivery_status,
          created_at,
          store_id
        `)
        .not('delivery_status', 'in', `(${RECEIPT_TERMINAL_STATUSES.join(',')})`)  // Closed orders don't need inventory linked
        .order('created_at', { ascending: false });

      // Filter by store if storeId provided
      if (storeId) {
        query = query.eq('store_id', storeId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const receiptsWithItems = await Promise.all(
        data.map(async (receipt) => {
          const { data: items } = await supabase
            .from('receipt_items')
            .select('*')
            .eq('receipt_id', receipt.id);
          return { ...receipt, items: items || [] };
        })
      );

      setReceipts(receiptsWithItems);
    } catch (err) {
      console.error('Error loading receipts:', err);
    }
  };

  // Start live camera with proper iOS/mobile support
  const startCamera = async () => {
    setCameraError(null);
    setCameraActive(true);
    setVideoReady(false);
    
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      // Request camera with mobile-friendly constraints
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Back camera
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 }
        },
        audio: false
      };
      
      console.log('Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Camera stream obtained:', stream.getVideoTracks()[0]?.getSettings());
      
      setCameraStream(stream);
      
    } catch (err) {
      console.error('Camera error:', err);
      setCameraActive(false);
      
      // Provide helpful error message
      let errorMsg = 'Could not access camera.';
      if (err.name === 'NotAllowedError') {
        errorMsg = 'Camera permission denied. Please allow camera access and try again.';
      } else if (err.name === 'NotFoundError') {
        errorMsg = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMsg = 'Camera is in use by another app.';
      }
      
      setCameraError(errorMsg);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped track:', track.kind);
      });
      setCameraStream(null);
    }
    setCameraActive(false);
    setVideoReady(false);
    setCameraError(null);
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Capture from live camera
  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Use actual video dimensions
    const width = video.videoWidth;
    const height = video.videoHeight;
    
    if (width === 0 || height === 0) {
      console.error('Video dimensions are zero');
      setError('Could not capture image. Please try again.');
      return;
    }
    
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setImagePreview(dataUrl);
    setImageData(dataUrl.split(',')[1]);
    
    stopCamera();
    processImage(dataUrl.split(',')[1]);
  };

  // Normalize a single File into { preview: dataUrl, base64: string } via canvas
  // Applies EXIF orientation and caps resolution for OCR
  const normalizeImageFile = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const normalizeCanvas = document.createElement('canvas');
        const MAX_DIM = 2048;
        let w = img.naturalWidth;
        let h = img.naturalHeight;

        if (w > MAX_DIM || h > MAX_DIM) {
          const scale = MAX_DIM / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }

        normalizeCanvas.width = w;
        normalizeCanvas.height = h;
        const ctx = normalizeCanvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        const dataUrl = normalizeCanvas.toDataURL('image/jpeg', 0.92);
        URL.revokeObjectURL(objectUrl);
        resolve({ preview: dataUrl, base64: dataUrl.split(',')[1] });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to load image: ${file.name}`));
      };

      img.src = objectUrl;
    });
  };

  // Handle file selection — supports single or multiple files
  // Camera input (capture="environment") always sends one file.
  // Gallery input (multiple) can send many — all get queued and processed sequentially.
  const handleFileSelect = async (event) => {
    console.log('📁 handleFileSelect triggered');
    const files = Array.from(event.target.files || []);
    console.log(`📁 Files selected: ${files.length}`);
    if (files.length === 0) return;

    const inputElement = event.target;

    // Validate all files upfront
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not an image file`);
        inputElement.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`"${file.name}" is too large (max 10MB)`);
        inputElement.value = '';
        return;
      }
    }

    // Single file — fast path (same as before, no queue UI)
    if (files.length === 1) {
      console.log('🖼️ Single file — normalizing...');
      try {
        const normalized = await normalizeImageFile(files[0]);
        setImagePreview(normalized.preview);
        setImageData(normalized.base64);
        processImage(normalized.base64);
      } catch (err) {
        setError(err.message || 'Failed to load image');
      }
      inputElement.value = '';
      return;
    }

    // Multiple files — normalize all, then batch process
    console.log(`🖼️ Normalizing ${files.length} images...`);
    setStep('processing');
    setIsProcessing(true);
    setError(null);

    try {
      const normalized = await Promise.all(files.map(f => normalizeImageFile(f)));
      console.log(`✅ All ${normalized.length} images normalized`);
      inputElement.value = '';
      await processImageBatch(normalized);
    } catch (err) {
      console.error('❌ Normalization error:', err);
      setError(err.message || 'Failed to load one or more images');
      setStep('capture');
      setIsProcessing(false);
      inputElement.value = '';
    }
  };

  // Process a batch of normalized images sequentially
  // Each image becomes a "page" — items are appended, invoice info taken from first page
  const processImageBatch = async (images) => {
    setStep('processing');
    setIsProcessing(true);
    setError(null);

    // Get auth token once for all requests
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      setError('Please sign in to scan invoices');
      setStep('capture');
      setIsProcessing(false);
      return;
    }

    let localPageCount = scannedPages.length; // Track locally since state is async
    let localItems = [...extractedItems];
    let localInvoiceInfo = { ...invoiceInfo };
    let localManufacturerId = overrideManufacturerId;
    let hasErrors = false;

    for (let i = 0; i < images.length; i++) {
      const { preview, base64 } = images[i];
      setQueueProgress({ current: i + 1, total: images.length });
      setImagePreview(preview);
      setImageData(base64);

      console.log(`📡 Processing page ${i + 1} of ${images.length}...`);

      try {
        const response = await fetch('/api/scan-invoice', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ image: base64, mediaType: 'image/jpeg', expectedSkus: expectedSkusPayload, storeId })
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          let errorMsg = result.error || `Failed to process page ${i + 1}`;
          if (result.details) errorMsg += `\n\nDetails: ${result.details}`;
          if (result.hint) errorMsg += `\n\n💡 ${result.hint}`;
          console.error(`❌ Page ${i + 1} failed:`, errorMsg);
          setError(errorMsg);
          hasErrors = true;
          continue; // Skip this page, try the next one
        }

        console.log(`✅ Page ${i + 1} processed successfully`);
        
        const isAdditionalPage = localPageCount > 0;
        const currentPageNumber = localPageCount + 1;

        // Invoice info from first successful page only
        if (!isAdditionalPage) {
          localInvoiceInfo = {
            supplierName: result.data.supplierName,
            invoiceNumber: result.data.invoiceNumber,
            invoiceDate: result.data.invoiceDate,
            billTo: result.data.billTo,
            shipTo: result.data.shipTo
          };
          setInvoiceInfo(localInvoiceInfo);

          // Auto-detect manufacturer
          const supplierName = result.data.supplierName?.trim().toLowerCase();
          if (supplierName && manufacturers.length > 0) {
            const normalize = (s) => s.toLowerCase().replace(/[\s\-_]/g, '');
            const normalizedSupplier = normalize(supplierName);
            const matched = manufacturers.find(m => {
              const normalizedMfr = normalize(m.name);
              return normalizedMfr === normalizedSupplier ||
                     normalizedMfr.includes(normalizedSupplier) ||
                     normalizedSupplier.includes(normalizedMfr);
            });
            localManufacturerId = matched ? matched.id : null;
            setOverrideManufacturerId(localManufacturerId);
          } else {
            localManufacturerId = null;
            setOverrideManufacturerId(null);
          }
        }

        // Tag and append items
        const newItems = (result.data.items || []).map(item => ({
          ...item,
          _page: currentPageNumber
        }));

        localItems = isAdditionalPage ? [...localItems, ...newItems] : newItems;
        setExtractedItems(localItems);

        // Track page
        localPageCount++;
        setScannedPages(prev => [...prev, {
          pageNumber: currentPageNumber,
          imagePreview: preview,
          itemCount: newItems.length
        }]);

      } catch (err) {
        console.error(`❌ Page ${i + 1} error:`, err);
        setError(`Page ${i + 1}: ${err.message || 'Failed to process'}`);
        hasErrors = true;
        continue;
      }
    }

    setQueueProgress(null);
    setIsProcessing(false);

    // Go to review if we got any items, otherwise back to capture
    if (localPageCount > 0) {
      setStep('review');
    } else {
      setStep('capture');
    }
  };

  // Process image with OCR
  const processImage = async (base64Data) => {
    console.log('🔄 processImage called');
    console.log('📊 Base64 data length:', base64Data?.length || 0);
    setStep('processing');
    setIsProcessing(true);
    setError(null);

    try {
      // Get current session for auth token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Please sign in to scan invoices');
      }

      console.log('📡 Sending to /api/scan-invoice...');
      const response = await fetch('/api/scan-invoice', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          image: base64Data,
          mediaType: 'image/jpeg',
          expectedSkus: expectedSkusPayload,
          storeId
        })
      });

      console.log('📡 Response status:', response.status);
      const result = await response.json();
      console.log('📡 Response result:', result.success ? 'Success' : 'Failed');

      if (!response.ok || !result.success) {
        let errorMsg = result.error || 'Failed to process invoice';
        if (result.details) {
          errorMsg += `\n\nDetails: ${result.details}`;
        }
        if (result.hint) {
          errorMsg += `\n\n💡 ${result.hint}`;
        }
        if (result.model_attempted) {
          errorMsg += `\n\nModel: ${result.model_attempted}`;
        }
        throw new Error(errorMsg);
      }

      console.log('✅ Invoice processed successfully');
      
      const isAdditionalPage = scannedPages.length > 0;
      const currentPageNumber = scannedPages.length + 1;
      
      // Only set invoice info on first page (or if it wasn't set yet)
      if (!isAdditionalPage) {
        setInvoiceInfo({
          supplierName: result.data.supplierName,
          invoiceNumber: result.data.invoiceNumber,
          invoiceDate: result.data.invoiceDate,
          billTo: result.data.billTo,
          shipTo: result.data.shipTo
        });
      }
      
      // Tag new items with page number and APPEND to existing items
      const newItems = (result.data.items || []).map(item => ({
        ...item,
        _page: currentPageNumber
      }));
      
      if (isAdditionalPage) {
        // Append to existing items
        setExtractedItems(prev => [...prev, ...newItems]);
      } else {
        // First page - replace
        setExtractedItems(newItems);
      }
      
      // Track this page
      setScannedPages(prev => [...prev, {
        pageNumber: currentPageNumber,
        imagePreview: imagePreview,
        itemCount: newItems.length
      }]);
      
      // Auto-detect manufacturer from supplier name on first page only
      if (!isAdditionalPage) {
        const supplierName = result.data.supplierName?.trim().toLowerCase();
        if (supplierName && manufacturers.length > 0) {
          const normalize = (s) => s.toLowerCase().replace(/[\s\-_]/g, '');
          const normalizedSupplier = normalize(supplierName);
          const matched = manufacturers.find(m => {
            const normalizedMfr = normalize(m.name);
            return normalizedMfr === normalizedSupplier || 
                   normalizedMfr.includes(normalizedSupplier) || 
                   normalizedSupplier.includes(normalizedMfr);
          });
          if (matched) {
            setOverrideManufacturerId(matched.id);
          } else {
            setOverrideManufacturerId(null);
          }
        } else {
          setOverrideManufacturerId(null);
        }
      }
      
      setStep('review');

    } catch (err) {
      console.error('OCR Error:', err);
      setError(err.message || 'Failed to extract invoice data. Please try again.');
      // If we already have items from previous pages, go back to review (not capture)
      // so user doesn't lose their work
      if (scannedPages.length > 0 && extractedItems.length > 0) {
        setStep('review');
      } else {
        setStep('capture');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Create new manufacturer inline from scanner review step
  const handleCreateScannerMfr = async () => {
    if (!newMfrName.trim() || !storeId) return;
    setIsCreatingMfr(true);
    try {
      const mfr = await createManufacturer(storeId, newMfrName.trim());
      if (mfr) {
        const refreshed = await fetchManufacturers(storeId);
        setManufacturers(refreshed || []);
        setOverrideManufacturerId(mfr.id);
      }
    } catch (err) {
      console.error('Error creating manufacturer:', err);
    } finally {
      setIsCreatingMfr(false);
      setShowNewMfrInput(false);
      setNewMfrName('');
    }
  };

  // Find matching receipts for an item SKU
  const findMatchingReceipts = (sku) => {
    if (!sku) return [];
    const normalizedSku = sku.toLowerCase().replace(/[-\s]/g, '');
    return receipts.filter(receipt => {
      // Exclude delivered/completed orders - they don't need new inventory linked
      if (RECEIPT_TERMINAL_STATUSES.includes(receipt.delivery_status)) return false;
      
      return receipt.items?.some(item => {
        const itemSku = (item.sku || '').toLowerCase().replace(/[-\s]/g, '');
        return itemSku.includes(normalizedSku) || normalizedSku.includes(itemSku);
      });
    });
  };

  // Handle correlation of an item to a receipt
  const handleCorrelate = (invoiceItem, receiptId, receiptItem) => {
    setCorrelations(prev => ({
      ...prev,
      [invoiceItem.sku]: {
        receiptId,
        receiptItemId: receiptItem?.id,
        customerName: receipts.find(r => r.id === receiptId)?.customer_name,
        receiptItemName: receiptItem?.item_name
      }
    }));
    setShowReceiptPicker(false);
    setSelectedItem(null);
  };

  // Remove correlation
  const removeCorrelation = (sku) => {
    setCorrelations(prev => {
      const updated = { ...prev };
      delete updated[sku];
      return updated;
    });
  };

  // Save invoice and correlations
  const saveInvoice = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // ========================================
      // GUARD A: Duplicate-invoice check
      // saveInvoice() creates a NEW supplier_invoices row every run, so a
      // re-scan of the same physical shipment would duplicate units. Before
      // creating anything, check whether this store already has an invoice
      // with this invoice number that produced inventory units. If so, STOP —
      // re-scanning would double-count. Store-scoped so we never read across
      // tenants. Best-effort: only blocks when we can positively confirm a
      // prior scan created units (needs a non-empty invoice number + storeId).
      // ========================================
      const dupInvoiceNumber = (invoiceInfo.invoiceNumber || '').trim();
      if (storeId && dupInvoiceNumber) {
        const { data: priorInvoices, error: priorErr } = await supabase
          .from('supplier_invoices')
          .select('id')
          .eq('store_id', storeId)
          .eq('invoice_number', dupInvoiceNumber);

        if (priorErr) {
          // Don't block saving on a failed lookup — log and continue.
          console.error('Guard A: prior-invoice lookup failed (continuing):', priorErr);
        } else if (priorInvoices && priorInvoices.length > 0) {
          const priorIds = priorInvoices.map(r => r.id);
          const { count: priorUnitCount, error: priorUnitErr } = await supabase
            .from('inventory_units')
            .select('id', { count: 'exact', head: true })
            .in('supplier_invoice_id', priorIds);

          if (priorUnitErr) {
            console.error('Guard A: prior-unit count failed (continuing):', priorUnitErr);
          } else if ((priorUnitCount || 0) > 0) {
            console.warn(`🛑 Guard A: invoice #${dupInvoiceNumber} already scanned — ${priorUnitCount} unit(s) exist. Blocking duplicate.`);
            setError(
              `Invoice #${dupInvoiceNumber} was already scanned for this store — ${priorUnitCount} inventory unit(s) already exist. ` +
              `Saving again would create duplicate units. If some items are missing, edit the existing inventory instead of re-scanning. ` +
              `(If you're certain this is a different shipment that reuses the same number, change the invoice number on the review screen and save again.)`
            );
            setIsProcessing(false);
            return;
          }
        }
      }
      // ======== END GUARD A ========

      const { data: invoice, error: invoiceError } = await supabase
        .from('supplier_invoices')
        .insert([{
          store_id: storeId || null,
          supplier_name: invoiceInfo.supplierName || null,
          invoice_number: invoiceInfo.invoiceNumber || null,
          invoice_date: invoiceInfo.invoiceDate || null,  // Convert empty string to null
          bill_to: invoiceInfo.billTo || null,
          ship_to: invoiceInfo.shipTo || null,
          scanned_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // ========================================
      // RESOLVE MANUFACTURER
      // Use override if user selected one, otherwise auto-resolve from supplier name
      // ========================================
      let manufacturerId = null;
      if (overrideManufacturerId) {
        // User explicitly selected a manufacturer in the review step
        manufacturerId = overrideManufacturerId;
        console.log(`🏭 Using override manufacturer: ${manufacturerId}`);
      } else if (storeId && invoiceInfo.supplierName?.trim()) {
        try {
          const mfr = await createManufacturer(storeId, invoiceInfo.supplierName.trim());
          if (mfr) {
            manufacturerId = mfr.id;
            console.log(`🏭 Resolved manufacturer: "${invoiceInfo.supplierName}" → ${mfr.id}`);
          }
        } catch (mfrErr) {
          console.error('Error resolving manufacturer:', mfrErr);
        }
      }

      const invoiceQtyMap = {};
      for (const item of extractedItems) {
        if (!item.sku) continue;
        const skuLower = item.sku.toLowerCase();
        invoiceQtyMap[skuLower] = (invoiceQtyMap[skuLower] || 0) + (item.quantity || 1);
      }
      
      const allMatches = [];
      const allocatedCount = {};
      
      for (const invoiceItem of extractedItems) {
        if (!invoiceItem.sku) continue;
        const skuLower = invoiceItem.sku.toLowerCase();
        const invoiceQty = invoiceItem.quantity || 1;
        
        // Query receipt_items - include manufacturer_id for smarter matching
        const { data: needingItems } = await supabase
          .from('receipt_items')
          .select('id, receipt_id, sku, item_name, manufacturer_id')
          .ilike('sku', invoiceItem.sku);
        
        if (!needingItems || needingItems.length === 0) continue;
        
        // Sort: exact manufacturer matches first, then legacy (null manufacturer), then mismatches last
        const sorted = [...needingItems].sort((a, b) => {
          const aScore = a.manufacturer_id === manufacturerId ? 0 : a.manufacturer_id === null ? 1 : 2;
          const bScore = b.manufacturer_id === manufacturerId ? 0 : b.manufacturer_id === null ? 1 : 2;
          return aScore - bScore;
        });
        
        for (const receiptItem of sorted) {
          // Skip items from a DIFFERENT manufacturer
          // If receipt item has a manufacturer, the scanned unit must match it
          if (receiptItem.manufacturer_id && receiptItem.manufacturer_id !== manufacturerId) {
            continue;
          }
          const { data: existingStatus } = await supabase
            .from('item_statuses')
            .select('status')
            .eq('receipt_id', receiptItem.receipt_id)
            .ilike('sku', invoiceItem.sku)
            .single();
          
          if (existingStatus?.status && ['at_store', 'out_for_delivery', 'delivered'].includes(existingStatus.status)) {
            continue;
          }
          
          allocatedCount[skuLower] = allocatedCount[skuLower] || 0;
          if (allocatedCount[skuLower] < invoiceQty) {
            allMatches.push({ invoiceItem, receiptItem });
            allocatedCount[skuLower]++;
          }
        }
      }

      console.log(`Found ${allMatches.length} auto-matches`);

      const itemsToInsert = extractedItems.map((item, index) => {
        const hasMatch = allMatches.some(m => 
          m.invoiceItem.sku?.toLowerCase() === item.sku?.toLowerCase()
        );
        const hasProblem = isProblematicSku(item.sku);
        return {
          supplier_invoice_id: invoice.id,
          sku: item.sku,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          amount: item.amount,
          weight: item.weight,
          position: index,
          status: hasProblem ? 'needs_review' : (hasMatch || correlations[item.sku] ? 'matched' : 'unmatched')
        };
      });

      const { error: itemsError } = await supabase
        .from('supplier_invoice_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      for (const match of allMatches) {
        const { invoiceItem, receiptItem } = match;
        
        await supabase
          .from('item_statuses')
          .upsert([{
            receipt_id: receiptItem.receipt_id,
            receipt_item_id: receiptItem.id,
            item_name: receiptItem.item_name || invoiceItem.description,
            sku: receiptItem.sku,
            status: 'at_store',
            notes: `Arrived via invoice #${invoiceInfo.invoiceNumber} from ${invoiceInfo.supplierName}`
          }], { onConflict: 'receipt_id,receipt_item_id' });

        await supabase
          .from('item_correlations')
          .insert([{
            supplier_invoice_id: invoice.id,
            supplier_sku: invoiceItem.sku,
            receipt_id: receiptItem.receipt_id,
            receipt_item_id: receiptItem.id,
            matched_at: new Date().toISOString()
          }]);
      }

      for (const [sku, correlation] of Object.entries(correlations)) {
        const alreadyMatched = allMatches.some(m => 
          m.invoiceItem.sku?.toLowerCase() === sku.toLowerCase() &&
          m.receiptItem.receipt_id === correlation.receiptId
        );
        if (alreadyMatched) continue;

        await supabase
          .from('item_correlations')
          .insert([{
            supplier_invoice_id: invoice.id,
            supplier_sku: sku,
            receipt_id: correlation.receiptId,
            receipt_item_id: correlation.receiptItemId,
            matched_at: new Date().toISOString()
          }]);

        const invoiceItem = extractedItems.find(i => i.sku === sku);
        await supabase
          .from('item_statuses')
          .upsert([{
            receipt_id: correlation.receiptId,
            receipt_item_id: correlation.receiptItemId,
            item_name: correlation.receiptItemName || invoiceItem?.description,
            sku: sku,
            status: 'at_store',
            notes: `Arrived via invoice #${invoiceInfo.invoiceNumber} from ${invoiceInfo.supplierName}`
          }], { onConflict: 'receipt_id,receipt_item_id' });
      }

      // ========================================
      // CREATE SERIALIZED INVENTORY UNITS
      // This creates a unique unit for each physical item
      // The database trigger auto-assigns to waiting orders (FIFO)
      // ========================================
      let unitsCreated = 0;
      let unitsAssigned = 0;
      // GUARD B: how many units SHOULD be created (sum of piece quantities).
      // Compared against unitsCreated after the loop to detect a silent
      // partial failure (loop's inner try/catch swallows per-piece errors).
      const expectedUnits = extractedItems.reduce(
        (sum, it) => sum + (it.sku ? (it.quantity || 1) : 0),
        0
      );

      // Normalized-SKU fallback support: the store's open receipt items,
      // fetched ONCE per save (lazily) and reused for every unit. Only used
      // when both exact lookups miss — see NORMALIZED FALLBACK below.
      let _waitingItemsCache = null;
      const fetchWaitingItemsOnce = async () => {
        if (_waitingItemsCache) return _waitingItemsCache;
        const { data } = await supabase
          .from('receipt_items')
          .select(`
            id,
            receipt_id,
            sku,
            item_name,
            manufacturer_id,
            receipts!inner(created_at, store_id, delivery_status)
          `)
          .eq('receipts.store_id', storeId)
          .not('receipts.delivery_status', 'in', `(${RECEIPT_TERMINAL_STATUSES.join(',')})`)
          .order('receipts(created_at)', { ascending: true });
        _waitingItemsCache = data || [];
        return _waitingItemsCache;
      };
      // Full equality after stripping separators — NEVER substring
      // (B1050-54 == B105054, but B549 must not match B549-21).
      const normSkuForMatch = (s) => (s || '').toUpperCase().replace(/[\s\-_\/]/g, '');

      // An item already marked as arrived/handled at the item level
      // (via the receipt's per-item status buttons) must never receive a
      // new unit — same status set the correlation loop already skips,
      // plus picked_up which is equally terminal.
      const isItemAlreadyArrived = async (receiptItemId) => {
        const { data } = await supabase
          .from('item_statuses')
          .select('status')
          .eq('receipt_item_id', receiptItemId)
          .maybeSingle();
        return !!data && ITEM_ARRIVED_STATUSES.includes(data.status);
      };

      if (storeId) {
        console.log('📦 Creating serialized inventory units...');

        // ========================================================
        // ATOMIC UNIT CREATION (replaces the old per-piece insert loop)
        // All units for this invoice are created in ONE transaction via the
        // create_invoice_units RPC: either every unit row is inserted or none
        // are. This eliminates the partial-write / duplicate-on-crash class of
        // failure entirely (Guards A/B are now belt-and-suspenders, not load-
        // bearing). Reservation of each created unit to a waiting receipt_item
        // is UNCHANGED and still runs per-unit in JS below, where it is already
        // safe and independent (a failed reservation just leaves a valid
        // available unit).
        // ========================================================
        const rpcItems = extractedItems
          .filter((it) => it.sku)
          .map((it) => ({
            sku: it.sku,
            description: it.description,
            quantity: it.quantity || 1,
            cost: it.unitPrice,
          }));

        let createdUnits = [];
        try {
          const { data: created, error: createErr } = await supabase.rpc('create_invoice_units', {
            p_store_id: storeId,
            p_supplier_invoice_id: invoice.id,
            p_manufacturer_id: manufacturerId,
            p_items: rpcItems,
            p_notes: `Arrived via invoice #${invoiceInfo.invoiceNumber} from ${invoiceInfo.supplierName}`,
          });
          if (createErr) throw createErr;
          // The RPC returns columns named out_id / out_sku / out_unit_serial
          // (prefixed to avoid shadowing inventory_units columns inside the
          // function). Normalize to the shape the reservation code below expects.
          createdUnits = (created || []).map((u) => ({
            id: u.out_id,
            sku: u.out_sku,
            unit_serial: u.out_unit_serial,
          }));
        } catch (createErr) {
          // Atomic: if creation failed, NOTHING was inserted. Surface it and
          // stop — do not proceed to reservation with a half-made batch.
          console.error('❌ Atomic unit creation failed (no units created):', createErr);
          setError('Failed to create inventory units — nothing was saved. Please try again.');
          setIsProcessing(false);
          return;
        }

        unitsCreated = createdUnits.length;
        console.log(`📦 Atomically created ${unitsCreated} unit(s) for invoice ${invoice.id}`);

        // Reserve each created unit against a waiting receipt_item. This is the
        // SAME three-tier matching the old loop used, run per created unit.
        for (const unit of createdUnits) {
          const unitSerial = unit.unit_serial;
          // Recover the source extracted item for this unit's SKU (for logs /
          // status notes). Matching by SKU is sufficient — reservation logic
          // only reads item.sku.
          const item = extractedItems.find(
            (it) => (it.sku || '').toUpperCase() === (unit.sku || '').toUpperCase()
          ) || { sku: unit.sku };
          try {
                
                // ========== FIX: Auto-assign to specific receipt_item ==========
                // MANUFACTURER-AWARE: Prefer matching (sku + manufacturer), 
                // then fall back to (sku + no manufacturer) for legacy items.
                // This prevents Ashley B549 from being assigned to a Coaster B549 order.
                let waitingItem = null;

                if (manufacturerId) {
                  // First: try exact match (same SKU AND same manufacturer)
                  // — only on receipts that haven't been delivered/closed
                  const { data: exactMatch } = await supabase
                    .from('receipt_items')
                    .select(`
                      id, 
                      receipt_id, 
                      sku, 
                      item_name,
                      manufacturer_id,
                      receipts!inner(created_at, store_id, delivery_status)
                    `)
                    .ilike('sku', item.sku)
                    .eq('manufacturer_id', manufacturerId)
                    .eq('receipts.store_id', storeId)
                    .not('receipts.delivery_status', 'in', `(${RECEIPT_TERMINAL_STATUSES.join(',')})`)
                    .order('receipts(created_at)', { ascending: true })
                    .limit(1)
                    .maybeSingle();

                  if (exactMatch) {
                    // Verify this item doesn't already have a unit AND
                    // hasn't been marked arrived/delivered at the item level
                    const { data: existingCheck } = await supabase
                      .from('inventory_units')
                      .select('id')
                      .eq('receipt_item_id', exactMatch.id)
                      .maybeSingle();
                    if (!existingCheck && !(await isItemAlreadyArrived(exactMatch.id))) {
                      waitingItem = exactMatch;
                      console.log(`🎯 Exact manufacturer match: ${item.sku} → receipt_item ${exactMatch.id}`);
                    }
                  }
                }

                if (!waitingItem) {
                  // Fallback: match SKU where manufacturer is NULL (legacy items without brand)
                  // — only on receipts that haven't been delivered/closed
                  const { data: legacyMatch } = await supabase
                    .from('receipt_items')
                    .select(`
                      id, 
                      receipt_id, 
                      sku, 
                      item_name,
                      manufacturer_id,
                      receipts!inner(created_at, store_id, delivery_status)
                    `)
                    .ilike('sku', item.sku)
                    .is('manufacturer_id', null)
                    .eq('receipts.store_id', storeId)
                    .not('receipts.delivery_status', 'in', `(${RECEIPT_TERMINAL_STATUSES.join(',')})`)
                    .order('receipts(created_at)', { ascending: true })
                    .limit(1)
                    .maybeSingle();

                  if (legacyMatch) {
                    const { data: existingCheck } = await supabase
                      .from('inventory_units')
                      .select('id')
                      .eq('receipt_item_id', legacyMatch.id)
                      .maybeSingle();
                    if (!existingCheck && !(await isItemAlreadyArrived(legacyMatch.id))) {
                      waitingItem = legacyMatch;
                      console.log(`📦 Legacy match (no manufacturer): ${item.sku} → receipt_item ${legacyMatch.id}`);
                    }
                  }
                }

                if (!waitingItem) {
                  // ========== NORMALIZED FALLBACK ==========
                  // Both exact lookups missed. Compare SKUs with separators
                  // stripped so format variants still link (B1050-54 vs
                  // B105054 — a real prod case). Rules: full equality after
                  // normalization ONLY (never substring), same manufacturer
                  // or legacy-null (same guard as the exact path), only
                  // undelivered receipts, FIFO by receipt date, and the same
                  // no-existing-unit verification.
                  const targetNorm = normSkuForMatch(item.sku);
                  if (targetNorm) {
                    const candidates = await fetchWaitingItemsOnce();
                    for (const ri of candidates) {
                      if (normSkuForMatch(ri.sku) !== targetNorm) continue;
                      if (ri.manufacturer_id && ri.manufacturer_id !== manufacturerId) continue;
                      if (await isItemAlreadyArrived(ri.id)) continue;
                      const { data: existingCheck } = await supabase
                        .from('inventory_units')
                        .select('id')
                        .eq('receipt_item_id', ri.id)
                        .maybeSingle();
                      if (!existingCheck) {
                        waitingItem = ri;
                        console.log(`🔀 Normalized SKU match: ${item.sku} → receipt_item ${ri.id} (receipt SKU "${ri.sku}")`);
                        break;
                      }
                    }
                  }
                  // ========== END NORMALIZED FALLBACK ==========
                }
                
                if (waitingItem) {
                  // waitingItem was already verified to not have an existing unit above
                  const { error: assignError } = await supabase
                    .from('inventory_units')
                    .update({
                      status: 'reserved',
                      reserved_for_receipt_id: waitingItem.receipt_id,
                      receipt_item_id: waitingItem.id,
                      reserved_at: new Date().toISOString()
                    })
                    .eq('id', unit.id);
                  
                  if (!assignError) {
                    unitsAssigned++;
                    console.log(`✅ Unit ${unitSerial}: assigned to receipt_item ${waitingItem.id} (${waitingItem.item_name})`);
                    
                    // Update item_statuses
                    await supabase
                      .from('item_statuses')
                      .upsert([{
                        receipt_id: waitingItem.receipt_id,
                        receipt_item_id: waitingItem.id,
                        item_name: waitingItem.item_name,
                        sku: item.sku,
                        status: 'at_store',
                        notes: `Auto-assigned from invoice #${invoiceInfo.invoiceNumber} (${invoiceInfo.supplierName || 'Unknown supplier'})`
                      }], { onConflict: 'receipt_id,receipt_item_id' });
                  } else {
                    console.log(`✅ Unit ${unitSerial}: available (assign error)`);
                  }
                } else {
                  console.log(`✅ Unit ${unitSerial}: available (no waiting orders for this SKU/manufacturer)`);
                }
                // ========== END FIX ==========

          } catch (unitErr) {
            console.error('Error reserving inventory unit:', unitErr);
          }
        }
        
        console.log(`📦 Created ${unitsCreated} units, ${unitsAssigned} auto-assigned to orders`);
      }
      // ======== END INVENTORY UNITS ========

      // ========================================
      // GUARD B: partial-failure detection
      // The unit-creation loop catches per-piece errors internally and keeps
      // going, so a timeout/hiccup can leave unitsCreated < expectedUnits with
      // no error thrown. Detect that here and surface it LOUDLY instead of
      // letting the modal close on a silent partial success. We still report
      // the partial result upstream (so the created units aren't "lost"), but
      // we flag it and do NOT auto-close, so the user sees the warning and
      // does not blindly re-scan (Guard A will also block a straight re-scan).
      // ========================================
      const unitsMissing = expectedUnits - unitsCreated;
      if (storeId && unitsMissing > 0) {
        console.warn(`⚠️ Guard B: expected ${expectedUnits} units, created ${unitsCreated} (${unitsMissing} missing).`);
        setError(
          `Partial save: ${unitsCreated} of ${expectedUnits} inventory units were created ` +
          `(${unitsMissing} did not save, likely a timeout). The invoice was recorded. ` +
          `Do NOT re-scan this invoice — that would duplicate the ${unitsCreated} units already created. ` +
          `Contact support or add the ${unitsMissing} missing unit(s) manually.`
        );

        if (onInvoiceProcessed) {
          onInvoiceProcessed({
            invoice,
            items: extractedItems,
            correlations,
            autoMatches: allMatches.length,
            unitsCreated,
            unitsAssigned,
            expectedUnits,
            partialFailure: true
          });
        }

        setIsProcessing(false);
        return; // stop before the success path / auto-close
      }
      // ======== END GUARD B ========

      if (onInvoiceProcessed) {
        onInvoiceProcessed({
          invoice,
          items: extractedItems,
          correlations,
          autoMatches: allMatches.length,
          unitsCreated,
          unitsAssigned
        });
      }

      if (onClose) {
        onClose();
      }

    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save invoice. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset scanner - full reset (clears everything)
  const resetScanner = () => {
    setStep('capture');
    setImageData(null);
    setImagePreview(null);
    setExtractedItems([]);
    setInvoiceInfo({});
    setCorrelations({});
    setError(null);
    setCameraError(null);
    setOverrideManufacturerId(null);
    setShowNewMfrInput(false);
    setNewMfrName('');
    setScannedPages([]);
    setQueueProgress(null);
    stopCamera();
  };

  // Add another page - keeps existing items, correlations, manufacturer, invoice info
  const addPage = () => {
    setStep('capture');
    setImageData(null);
    setImagePreview(null);
    setError(null);
    setCameraError(null);
    stopCamera();
  };

  // Filter receipts by search - exclude delivered orders (they don't need inventory linked)
  const filteredReceipts = (searchQuery
    ? receipts.filter(r => 
        r.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.customer_phone?.includes(searchQuery) ||
        r.items?.some(i => i.sku?.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : receipts
  ).filter(r => r.delivery_status !== 'delivered');

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      processing: 'bg-blue-100 text-blue-600 border-blue-300',
      in_transit: 'bg-blue-100 text-blue-600 border-blue-300',
      out_for_delivery: 'bg-blue-100 text-blue-600 border-blue-300',
      delivered: 'bg-emerald-100 text-emerald-600 border-emerald-300'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      processing: Package,
      in_transit: Truck,
      out_for_delivery: Truck,
      delivered: Home
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-3 h-3" />;
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 z-[100] overflow-hidden flex items-end sm:items-stretch sm:justify-end scanner-drawer-backdrop"
      style={{ touchAction: 'none' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          stopCamera();
          onClose?.();
        }
      }}
    >
      <style>{`
        @keyframes scannerDrawerSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes scannerDrawerSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes scannerDrawerBackdropFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .scanner-drawer-backdrop { animation: scannerDrawerBackdropFade 180ms ease-out; }
        /* Mobile: bottom-sheet slide-up. Desktop: right-side drawer slide-in. */
        .scanner-drawer-panel { animation: scannerDrawerSlideUp 240ms cubic-bezier(0.32, 0.72, 0, 1); }
        @media (min-width: 640px) {
          .scanner-drawer-panel { animation: scannerDrawerSlideIn 240ms cubic-bezier(0.32, 0.72, 0, 1); }
        }
      `}</style>
      {/*
        Mobile: bottom sheet (rounded-t-3xl, border-t, respects safe-area).
        Desktop: right-side drawer matching the receipt creation drawer
          (sm:h-screen, sm:rounded-none, sm:border-l, slides in from right).
      */}
      <div
        className="bg-white w-full rounded-t-3xl sm:rounded-none border-t sm:border-t-0 sm:border-l border-slate-200 sm:border-slate-300 max-h-[calc(100vh-env(safe-area-inset-top,0px)-12px)] sm:max-h-screen sm:h-screen sm:w-[560px] md:w-[640px] lg:w-[720px] xl:w-[780px] flex flex-col shadow-2xl overflow-hidden scanner-drawer-panel"
        style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}
      >
        
        {/* Drag Handle - Mobile only */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        {/* Header */}
        <div className="px-5 pt-2 pb-4 sm:p-4 border-b-2 border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-400/30 flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Invoice Scanner</h2>
              <p className="text-xs text-slate-600">
                {step === 'capture' && !cameraActive && scannedPages.length === 0 && 'Take a photo or upload an invoice'}
                {step === 'capture' && !cameraActive && scannedPages.length > 0 && `Scan page ${scannedPages.length + 1} — ${extractedItems.length} items from ${scannedPages.length} page${scannedPages.length > 1 ? 's' : ''} so far`}
                {step === 'capture' && cameraActive && 'Position invoice in frame'}
                {step === 'processing' && (queueProgress ? `Scanning page ${queueProgress.current} of ${queueProgress.total}...` : 'Extracting invoice data...')}
                {step === 'review' && `Review and correlate items${scannedPages.length > 1 ? ` (${scannedPages.length} pages)` : ''}`}
              </p>
            </div>
          </div>
          <button
            onClick={() => { stopCamera(); onClose?.(); }}
            className="p-2 rounded-lg bg-red-100 border border-red-400/30 text-red-600 hover:bg-red-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400/30 rounded-xl flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-red-600 text-sm whitespace-pre-wrap break-words">{error}</p>
                <button 
                  onClick={() => setError(null)}
                  className="text-xs text-red-300 hover:text-red-200 mt-1"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Step: Capture */}
          {step === 'capture' && (
            <div className="space-y-4">
              
              {/* Back to Review banner - shown when adding additional pages */}
              {scannedPages.length > 0 && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    <span className="text-indigo-800 text-sm font-medium">
                      {extractedItems.length} items from {scannedPages.length} page{scannedPages.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={() => setStep('review')}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Back to Review
                  </button>
                </div>
              )}
              
              {/* ==========================================
                  LIVE CAMERA VIEW WITH SCANNER UI
              ========================================== */}
              {cameraActive && (
                <div className="relative rounded-2xl overflow-hidden bg-black" style={{ minHeight: '350px' }}>
                  {/* Video Element */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    webkit-playsinline="true"
                    className="w-full h-full object-cover"
                    style={{ minHeight: '350px', transform: 'scaleX(1)' }}
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Loading overlay while video initializes */}
                  {!videoReady && !cameraError && (
                    <div className="absolute inset-0 bg-black flex flex-col items-center justify-center">
                      <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
                      <p className="text-slate-600 text-sm">Starting camera...</p>
                    </div>
                  )}
                  
                  {/* Camera error */}
                  {cameraError && (
                    <div className="absolute inset-0 bg-black flex flex-col items-center justify-center p-4">
                      <AlertCircle className="w-12 h-12 text-red-600 mb-3" />
                      <p className="text-red-600 text-center text-sm mb-4">{cameraError}</p>
                      <button
                        onClick={() => { stopCamera(); }}
                        className="px-4 py-2 bg-slate-100 rounded-lg text-slate-700 text-sm"
                      >
                        Go Back
                      </button>
                    </div>
                  )}
                  
                  {/* Scanner Overlay UI - Only show when video is ready */}
                  {videoReady && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Corner brackets */}
                      <div className="absolute inset-6">
                        {/* Top-left corner */}
                        <div className="absolute top-0 left-0 w-12 h-12 border-l-3 border-t-3 border-blue-400 rounded-tl-lg" style={{ borderWidth: '3px 0 0 3px' }} />
                        {/* Top-right corner */}
                        <div className="absolute top-0 right-0 w-12 h-12 border-r-3 border-t-3 border-blue-400 rounded-tr-lg" style={{ borderWidth: '3px 3px 0 0' }} />
                        {/* Bottom-left corner */}
                        <div className="absolute bottom-0 left-0 w-12 h-12 border-l-3 border-b-3 border-blue-400 rounded-bl-lg" style={{ borderWidth: '0 0 3px 3px' }} />
                        {/* Bottom-right corner */}
                        <div className="absolute bottom-0 right-0 w-12 h-12 border-r-3 border-b-3 border-blue-400 rounded-br-lg" style={{ borderWidth: '0 3px 3px 0' }} />
                      </div>
                      
                      {/* Center target area */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <div className="w-72 h-48 border-2 border-blue-400/60 rounded-xl flex flex-col items-center justify-center bg-slate-900/20">
                          <ScanLine className="w-8 h-8 text-blue-600 mb-2" />
                          <p className="text-blue-600 text-sm font-medium">Center invoice here</p>
                        </div>
                      </div>
                      
                      {/* Animated scanning line */}
                      <div className="absolute left-6 right-6 overflow-hidden" style={{ top: '15%', bottom: '15%' }}>
                        <div 
                          className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent shadow-lg shadow-blue-400/50"
                          style={{ 
                            animation: 'scanMove 2s ease-in-out infinite',
                          }}
                        />
                      </div>
                      
                      {/* Grid lines */}
                      <div className="absolute inset-12 opacity-20">
                        <div className="absolute top-1/3 left-0 right-0 h-px bg-blue-400" />
                        <div className="absolute top-2/3 left-0 right-0 h-px bg-blue-400" />
                        <div className="absolute left-1/3 top-0 bottom-0 w-px bg-blue-400" />
                        <div className="absolute left-2/3 top-0 bottom-0 w-px bg-blue-400" />
                      </div>
                    </div>
                  )}

                  {/* Capture button */}
                  {videoReady && (
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 pointer-events-auto">
                      <button
                        onClick={captureFromCamera}
                        className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 transition-transform border-4 border-white/30"
                      >
                        <Aperture className="w-10 h-10 text-white" />
                      </button>
                    </div>
                  )}

                  {/* Cancel camera button */}
                  <button
                    onClick={stopCamera}
                    className="absolute top-4 right-4 p-2.5 bg-slate-100 backdrop-blur rounded-xl text-white hover:bg-slate-900/50 transition-colors pointer-events-auto"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  {/* Camera mode indicator */}
                  {videoReady && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-slate-100 backdrop-blur rounded-lg flex items-center space-x-2 pointer-events-none">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-slate-700 text-xs font-medium">Live</span>
                    </div>
                  )}
                </div>
              )}

              {/* Capture options (when camera not active) */}
              {!cameraActive && (
                <div className="space-y-4">
                  {/* Image preview if we have one */}
                  {imagePreview && (
                    <div className="relative rounded-2xl overflow-hidden">
                      <img src={imagePreview} alt="Invoice preview" className="w-full rounded-2xl" />
                      <button
                        onClick={resetScanner}
                        className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-lg text-white hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Capture buttons */}
                  {!imagePreview && (
                    <>
                      {/* 
                        MOBILE FIX: Use opacity-0 + absolute positioning instead of display:none
                        iOS Safari has issues with display:none inputs - onChange doesn't fire reliably
                      */}
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => {
                          console.log('📷 Camera input onChange fired');
                          handleFileSelect(e);
                        }}
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          whiteSpace: 'nowrap',
                          border: 0
                        }}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          console.log('📁 File input onChange fired');
                          handleFileSelect(e);
                        }}
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          whiteSpace: 'nowrap',
                          border: 0
                        }}
                      />
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Camera button - Only functional on mobile */}
                        {isMobile ? (
                          <button
                            type="button"
                            onClick={() => {
                              console.log('📷 Camera button clicked');
                              cameraInputRef.current?.click();
                            }}
                            className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-400/30 rounded-2xl hover:border-blue-400/50 hover:shadow-lg hover:shadow-blue-500/20 transition-all group active:scale-[0.98] cursor-pointer"
                          >
                            <div className="relative mb-3">
                              <Camera className="w-12 h-12 text-blue-600 group-hover:scale-110 transition-transform" />
                              {/* Mini scanner effect */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-8 h-0.5 bg-blue-400/50 animate-pulse" />
                              </div>
                            </div>
                            <span className="text-slate-800 font-semibold">Take Photo</span>
                            <span className="text-slate-500 text-xs mt-1">Use camera</span>
                          </button>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-gray-500/10 to-gray-600/10 border border-gray-500/20 rounded-2xl">
                            <div className="relative mb-3">
                              <Smartphone className="w-12 h-12 text-slate-500" />
                            </div>
                            <span className="text-slate-500 font-semibold">Take Photo</span>
                            <span className="text-slate-400 text-xs mt-1 text-center px-2">
                              Use mobile app to capture with camera
                            </span>
                          </div>
                        )}

                        {/* File upload button - using programmatic click for reliable mobile support */}
                        <button
                          type="button"
                          onClick={() => {
                            console.log('📁 Gallery button clicked');
                            fileInputRef.current?.click();
                          }}
                          className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-blue-50 to-slate-50 border border-slate-300 rounded-2xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/20 transition-all group active:scale-[0.98] cursor-pointer"
                        >
                          <Upload className="w-12 h-12 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
                          <span className="text-slate-800 font-semibold">Upload Images</span>
                          <span className="text-slate-500 text-xs mt-1">Select one or more pages</span>
                        </button>
                      </div>
                    </>
                  )}

                  {/* AI Scanner Info Panel */}
                  <div className="space-y-3">
                    {/* Supported Suppliers */}
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-200 rounded-xl">
                      <div className="flex items-center space-x-2 mb-3">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <h4 className="text-slate-800 font-semibold text-sm">Supported Suppliers</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {['HomeElegance', 'Coaster', 'Ashley', 'Kingdom', 'FLEXSTEEL'].map((supplier) => (
                          <span 
                            key={supplier}
                            className="px-3 py-1 bg-white border border-blue-200 rounded-full text-xs font-medium text-blue-700"
                          >
                            {supplier}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Verification Warning */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <ShieldAlert className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="text-amber-800 font-semibold text-sm">Always Verify Results</h4>
                          <p className="text-amber-700 text-xs mt-1">
                            AI scanning may occasionally miss or misread items. Please review all extracted data before saving.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Request New Template */}
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-slate-800 font-semibold text-sm">Need a Different Supplier?</h4>
                          <p className="text-slate-600 text-xs mt-1">
                            Send us a sample invoice and we'll try our best to get the AI trained for your new invoice within 1 business day.
                          </p>
                          <a 
                            href="mailto:support@exitiumlabs.com?subject=New Invoice Template Request"
                            className="inline-flex items-center space-x-1.5 mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            <Mail className="w-3 h-3" />
                            <span>support@exitiumlabs.com</span>
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Quick Tips */}
                    <div className="p-3 bg-blue-500/10 border border-blue-400/20 rounded-xl">
                      <h4 className="text-blue-600 font-semibold text-xs mb-1.5">📸 Quick Tips</h4>
                      <p className="text-slate-600 text-xs">
                        Good lighting • Flat surface • Include all item rows
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <div className="flex flex-col items-center py-2">

              {/* ===== AI Scanner Stage ===== */}
              <div className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 shadow-[0_0_70px_-12px_rgba(37,99,235,0.55)]">

                {/* Invoice image — the hero */}
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Scanning invoice"
                    className="w-full max-h-[55vh] object-contain opacity-80"
                    style={{ filter: 'contrast(1.05) saturate(0.85)' }}
                  />
                ) : (
                  <div className="w-full h-72 flex items-center justify-center">
                    <FileText className="w-16 h-16 text-slate-700" />
                  </div>
                )}

                {/* Holographic grid overlay */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(59,130,246,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.08) 1px, transparent 1px)',
                    backgroundSize: '26px 26px',
                  }}
                />

                {/* Sweeping scan beam */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div
                    className="absolute inset-x-0 h-full"
                    style={{ animation: 'scanBeamSweep 2.6s cubic-bezier(0.45, 0, 0.55, 1) infinite' }}
                  >
                    <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent via-blue-400/20 to-blue-300/60" />
                    <div className="absolute bottom-0 inset-x-0 h-[2px] bg-blue-400 shadow-[0_0_24px_5px_rgba(59,130,246,0.9)]" />
                  </div>
                </div>

                {/* AI detection boxes — flash as if locking onto line items */}
                <div className="absolute border-2 border-blue-400/90 rounded-sm shadow-[0_0_12px_rgba(59,130,246,0.55)] pointer-events-none" style={{ top: '18%', left: '10%', width: '36%', height: '4.5%', animation: 'detectFlash 3.4s ease-in-out infinite', animationDelay: '0.3s' }} />
                <div className="absolute border-2 border-blue-400/90 rounded-sm shadow-[0_0_12px_rgba(59,130,246,0.55)] pointer-events-none" style={{ top: '34%', left: '52%', width: '30%', height: '4%', animation: 'detectFlash 3.4s ease-in-out infinite', animationDelay: '1.2s' }} />
                <div className="absolute border-2 border-blue-500/90 rounded-sm shadow-[0_0_12px_rgba(37,99,235,0.55)] pointer-events-none" style={{ top: '49%', left: '14%', width: '44%', height: '4.5%', animation: 'detectFlash 3.4s ease-in-out infinite', animationDelay: '2s' }} />
                <div className="absolute border-2 border-blue-400/90 rounded-sm shadow-[0_0_12px_rgba(59,130,246,0.55)] pointer-events-none" style={{ top: '66%', left: '40%', width: '34%', height: '4%', animation: 'detectFlash 3.4s ease-in-out infinite', animationDelay: '2.7s' }} />

                {/* Viewfinder corner brackets */}
                <div className="absolute top-2.5 left-2.5 w-7 h-7 border-t-2 border-l-2 border-blue-500 rounded-tl-md pointer-events-none" style={{ animation: 'cornerPulse 2s ease-in-out infinite' }} />
                <div className="absolute top-2.5 right-2.5 w-7 h-7 border-t-2 border-r-2 border-blue-500 rounded-tr-md pointer-events-none" style={{ animation: 'cornerPulse 2s ease-in-out infinite', animationDelay: '0.5s' }} />
                <div className="absolute bottom-2.5 left-2.5 w-7 h-7 border-b-2 border-l-2 border-blue-500 rounded-bl-md pointer-events-none" style={{ animation: 'cornerPulse 2s ease-in-out infinite', animationDelay: '1s' }} />
                <div className="absolute bottom-2.5 right-2.5 w-7 h-7 border-b-2 border-r-2 border-blue-500 rounded-br-md pointer-events-none" style={{ animation: 'cornerPulse 2s ease-in-out infinite', animationDelay: '1.5s' }} />

                {/* Status chip */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-slate-900/85 backdrop-blur-sm border border-blue-400/40 flex items-center gap-2 pointer-events-none">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" style={{ animation: 'statusBlink 1.2s ease-in-out infinite' }} />
                  <span className="text-blue-300 text-[10px] font-mono font-semibold tracking-[0.2em]">
                    {queueProgress ? `AI INK VERIFICATION · PAGE ${queueProgress.current}/${queueProgress.total}` : 'AI INK VERIFICATION'}
                  </span>
                </div>
              </div>

              {/* ===== Phase tracker ===== */}
              <div className="mt-5 w-full max-w-md mx-auto space-y-2">
                {PROCESSING_PHASES.map((phase, idx) => {
                  const isDone = idx < processingPhase;
                  const isCurrent = idx === processingPhase;
                  return (
                    <div
                      key={phase.label}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-all duration-500 ${
                        isCurrent
                          ? 'bg-blue-50 border-blue-200'
                          : isDone
                            ? 'bg-blue-50/50 border-blue-100'
                            : 'bg-slate-50 border-slate-100 opacity-50'
                      }`}
                    >
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        {isDone ? (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        ) : isCurrent ? (
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-slate-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold leading-tight ${isCurrent ? 'text-blue-900' : isDone ? 'text-blue-800' : 'text-slate-500'}`}>
                          {phase.label}
                        </p>
                        {isCurrent && (
                          <p className="text-xs text-blue-600/80 mt-0.5">{phase.sub}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Batch progress bar */}
              {queueProgress && queueProgress.total > 1 && (
                <div className="mt-4 w-full max-w-md mx-auto">
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${(queueProgress.current / queueProgress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-slate-500 text-xs mt-1.5 text-center">
                    {queueProgress.current} / {queueProgress.total} pages
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step: Review */}
          {step === 'review' && (
            <div className="space-y-4">
              {/* Invoice Info */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-slate-900 font-bold text-lg">{invoiceInfo.supplierName || 'Unknown Supplier'}</h3>
                    <p className="text-slate-600 text-sm">Invoice #{invoiceInfo.invoiceNumber || 'N/A'}</p>
                    {invoiceInfo.invoiceDate && (
                      <p className="text-slate-500 text-xs mt-1">Date: {invoiceInfo.invoiceDate}</p>
                    )}
                  </div>
                  <button
                    onClick={resetScanner}
                    className="p-2 bg-slate-100 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                {/* Manufacturer Override */}
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1.5">
                    <Building2 className="w-3.5 h-3.5 text-purple-500" />
                    Manufacturer / Brand <span className="text-red-500">*</span>
                  </label>
                  {!showNewMfrInput ? (
                    <select
                      value={overrideManufacturerId || ''}
                      onChange={(e) => {
                        if (e.target.value === '__new__') {
                          setShowNewMfrInput(true);
                          setNewMfrName(invoiceInfo.supplierName || '');
                        } else {
                          setOverrideManufacturerId(e.target.value || null);
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        !overrideManufacturerId ? 'border-red-300 bg-red-50' : 'border-slate-300'
                      }`}
                    >
                      <option value="">— Select manufacturer —</option>
                      {manufacturers.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                      <option value="__new__">+ Add new manufacturer...</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newMfrName}
                        onChange={(e) => setNewMfrName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newMfrName.trim()) {
                            e.preventDefault();
                            handleCreateScannerMfr();
                          }
                          if (e.key === 'Escape') {
                            setShowNewMfrInput(false);
                            setNewMfrName('');
                          }
                        }}
                        placeholder="e.g., Homelegance"
                        className="flex-1 px-3 py-2 bg-white border border-purple-300 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                        autoFocus
                        disabled={isCreatingMfr}
                      />
                      <button
                        onClick={handleCreateScannerMfr}
                        disabled={!newMfrName.trim() || isCreatingMfr}
                        className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-purple-700 transition-colors"
                      >
                        {isCreatingMfr ? '...' : 'Add'}
                      </button>
                      <button
                        onClick={() => { setShowNewMfrInput(false); setNewMfrName(''); }}
                        className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {!overrideManufacturerId && !showNewMfrInput && invoiceInfo.supplierName && (
                    <p className="text-xs text-amber-600 mt-1">
                      AI detected "<strong>{invoiceInfo.supplierName}</strong>" — select a match above or add it as a new manufacturer
                    </p>
                  )}
                  {overrideManufacturerId && (
                    <p className="text-xs text-emerald-600 mt-1">
                      ✓ All scanned units will be tagged as {manufacturers.find(m => m.id === overrideManufacturerId)?.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Scanned Pages Summary */}
              {scannedPages.length > 1 && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    <span className="text-indigo-800 font-semibold text-sm">{scannedPages.length} Pages Scanned</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {scannedPages.map((page) => (
                      <span key={page.pageNumber} className="px-2.5 py-1 bg-white border border-indigo-200 rounded-lg text-xs text-indigo-700 font-medium">
                        Page {page.pageNumber}: {page.itemCount} items
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Extracted Items */}
              <div className="space-y-2">
                <h4 className="text-slate-800 font-semibold flex items-center space-x-2">
                  <Package className="w-4 h-4 text-blue-600" />
                  <span>Items Found ({extractedItems.length})</span>
                </h4>

                {extractedItems.map((item, index) => {
                  const correlation = correlations[item.sku];
                  const potentialMatches = findMatchingReceipts(item.sku);
                  const hasProblematicSku = isProblematicSku(item.sku);

                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border transition-all ${
                        hasProblematicSku
                          ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200'
                          : correlation
                          ? 'bg-emerald-50 border-emerald-300'
                          : potentialMatches.length > 0
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {/* Editable SKU & Quantity row */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="text"
                              value={item.sku || ''}
                              onChange={(e) => {
                                const newSku = e.target.value;
                                // If there was a correlation on the old SKU, move it
                                const oldSku = item.sku;
                                setExtractedItems(prev => prev.map((it, i) => 
                                  i === index ? { ...it, sku: newSku } : it
                                ));
                                if (oldSku && correlations[oldSku] && newSku !== oldSku) {
                                  setCorrelations(prev => {
                                    const updated = { ...prev };
                                    updated[newSku] = updated[oldSku];
                                    delete updated[oldSku];
                                    return updated;
                                  });
                                }
                              }}
                              className={`font-mono text-sm px-2 py-0.5 rounded border outline-none transition-all w-32 min-w-0 ${
                                hasProblematicSku
                                  ? 'text-amber-700 bg-amber-100 border-amber-400 ring-2 ring-amber-300 hover:bg-amber-50 focus:bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500'
                                  : 'text-blue-600 bg-blue-400/10 hover:bg-blue-400/20 focus:bg-white focus:ring-2 focus:ring-blue-400 border-transparent focus:border-blue-300'
                              }`}
                              title={hasProblematicSku ? "⚠️ Item not found — tap to enter the correct item number" : "Click to edit SKU"}
                              placeholder={hasProblematicSku ? "Enter item #..." : ""}
                            />
                            {/* Warning icon for problematic SKUs */}
                            {hasProblematicSku && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-200 text-amber-700 rounded text-[10px] font-bold" title="This item number needs to be corrected">
                                <AlertCircle className="w-3 h-3" />
                                Fix
                              </span>
                            )}
                            {/* Page badge - only show when multi-page */}
                            {scannedPages.length > 1 && item._page && (
                              <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-600 text-[10px] font-bold rounded">
                                P{item._page}
                              </span>
                            )}
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500 text-xs">Qty:</span>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity || 1}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  setExtractedItems(prev => prev.map((it, i) => 
                                    i === index ? { ...it, quantity: val } : it
                                  ));
                                }}
                                className="text-slate-700 text-xs bg-transparent hover:bg-slate-100 focus:bg-white focus:ring-2 focus:ring-blue-400 px-1.5 py-0.5 rounded border border-transparent focus:border-blue-300 outline-none transition-all w-14"
                                title="Click to edit quantity"
                              />
                            </div>
                            {/* Delete item button */}
                            <button
                              onClick={() => {
                                const sku = item.sku;
                                setExtractedItems(prev => prev.filter((_, i) => i !== index));
                                if (sku && correlations[sku]) {
                                  setCorrelations(prev => {
                                    const updated = { ...prev };
                                    delete updated[sku];
                                    return updated;
                                  });
                                }
                              }}
                              className="p-0.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Remove item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {/* Editable description */}
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => {
                              setExtractedItems(prev => prev.map((it, i) => 
                                i === index ? { ...it, description: e.target.value } : it
                              ));
                            }}
                            className="text-slate-700 text-sm mt-1 w-full bg-transparent hover:bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-400 px-2 py-0.5 rounded border border-transparent focus:border-blue-300 outline-none transition-all"
                            placeholder="Description..."
                            title="Click to edit description"
                          />
                        </div>

                        <div className="flex-shrink-0 ml-3">
                          {correlation ? (
                            <div className="flex items-center space-x-2">
                              <div className="text-right">
                                <p className="text-emerald-600 text-xs font-semibold">Matched</p>
                                <p className="text-slate-600 text-xs">{correlation.customerName}</p>
                              </div>
                              <button
                                onClick={() => removeCorrelation(item.sku)}
                                className="p-1.5 bg-red-100 rounded-lg text-red-600 hover:bg-red-200"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setShowReceiptPicker(true);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1 ${
                                potentialMatches.length > 0
                                  ? 'bg-blue-100 border border-blue-300 text-blue-600 hover:bg-blue-200'
                                  : 'bg-slate-100 border border-slate-300 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              <Link2 className="w-3 h-3" />
                              <span>
                                {potentialMatches.length > 0 
                                  ? `${potentialMatches.length} possible` 
                                  : 'Link'
                                }
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add Item Button */}
              <button
                onClick={() => {
                  const currentPage = scannedPages.length || 1;
                  setExtractedItems(prev => [...prev, { sku: '', description: '', quantity: 1, unitPrice: null, amount: null, weight: null, _page: currentPage }]);
                }}
                className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all text-sm font-medium flex items-center justify-center gap-1.5"
              >
                <span className="text-lg leading-none">+</span>
                Add Item
              </button>

              {/* Summary */}
              {/* ⚠️ Unresolved Items Warning Banner */}
              {problemItemCount > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-amber-800 font-semibold">
                        {problemItemCount} item{problemItemCount !== 1 ? 's' : ''} need{problemItemCount === 1 ? 's' : ''} attention
                      </p>
                      <p className="text-amber-700 text-xs mt-1">
                        {problemItemCount === 1 ? 'An item has' : 'Some items have'} an unrecognized item number. 
                        Tap the highlighted SKU field to type the correct number before saving, 
                        or save now and fix it later from the Inventory tab.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-4 bg-blue-50 border border-slate-300 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-800 font-semibold">
                      {Object.keys(correlations).length} of {extractedItems.length} items matched
                    </p>
                    <p className="text-slate-600 text-xs mt-1">
                      Matched items will be marked as "At Store" in customer orders
                    </p>
                  </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    Object.keys(correlations).length === extractedItems.length
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {Object.keys(correlations).length === extractedItems.length
                      ? <CheckCircle className="w-4 h-4" />
                      : <span className="text-xs font-bold">{Object.keys(correlations).length}</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {step === 'review' && (
          <div className="p-4 border-t border-slate-200 flex-shrink-0">
            {/* Inline warning reminder above buttons */}
            {problemItemCount > 0 && (
              <div className="flex items-center gap-2 mb-3 px-1">
                <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <p className="text-amber-600 text-xs font-medium">
                  {problemItemCount} unresolved item{problemItemCount !== 1 ? 's' : ''} — you can still save and fix later
                </p>
              </div>
            )}
            <div className="flex space-x-3">
              <button
                onClick={addPage}
                className="flex-1 py-3 px-4 bg-slate-100 border border-slate-300 rounded-xl text-slate-700 font-semibold hover:bg-slate-200 transition-colors flex items-center justify-center gap-1.5"
              >
                <FileText className="w-4 h-4" />
                <span>+ Add Page {scannedPages.length > 0 ? `(${scannedPages.length} scanned)` : ''}</span>
              </button>
              <button
                onClick={saveInvoice}
                disabled={isProcessing || !overrideManufacturerId}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-2 ${
                  problemItemCount > 0
                    ? 'bg-amber-500 text-white hover:shadow-amber-500/20'
                    : 'bg-blue-600 text-white hover:shadow-blue-500/20'
                }`}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    {problemItemCount > 0 ? (
                      <AlertCircle className="w-4 h-4" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    <span>{problemItemCount > 0 ? `Save (${problemItemCount} to fix)` : 'Save Invoice'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Receipt Picker Modal */}
      {showReceiptPicker && selectedItem && (
        <div className="fixed inset-0 bg-slate-900/50 z-[110] flex items-center justify-center p-4">
          <div className="w-full max-w-lg max-h-[80vh] bg-white rounded-3xl border border-slate-200 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-slate-800 font-bold">Link to Customer Order</h3>
                  <p className="text-slate-600 text-sm">SKU: {selectedItem.sku}</p>
                </div>
                <button
                  onClick={() => { setShowReceiptPicker(false); setSelectedItem(null); }}
                  className="p-2 bg-slate-100 rounded-lg text-slate-600 hover:text-slate-900"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-3 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type="text"
                  placeholder="Search customers or SKUs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredReceipts.map(receipt => {
                const matchingItems = receipt.items?.filter(item => {
                  const itemSku = (item.sku || '').toLowerCase().replace(/[-\s]/g, '');
                  const searchSku = (selectedItem.sku || '').toLowerCase().replace(/[-\s]/g, '');
                  return itemSku.includes(searchSku) || searchSku.includes(itemSku);
                });

                return (
                  <div
                    key={receipt.id}
                    className="p-3 bg-white border border-slate-200 rounded-xl hover:border-blue-400/30 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-slate-800 font-semibold">{receipt.customer_name}</p>
                        <p className="text-slate-600 text-xs">{receipt.customer_phone}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-lg text-xs font-semibold flex items-center space-x-1 border ${getStatusColor(receipt.delivery_status)}`}>
                        {getStatusIcon(receipt.delivery_status)}
                        <span className="capitalize">{receipt.delivery_status?.replace('_', ' ')}</span>
                      </span>
                    </div>

                    <div className="space-y-1">
                      {(matchingItems?.length > 0 ? matchingItems : receipt.items?.slice(0, 3))?.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleCorrelate(selectedItem, receipt.id, item)}
                          className={`w-full text-left p-2 rounded-lg flex items-center justify-between group ${
                            matchingItems?.some(m => m.id === item.id)
                              ? 'bg-blue-100 border border-blue-400/30'
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-600 text-sm truncate">{item.item_name}</p>
                            <p className="text-slate-500 text-xs">SKU: {item.sku}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-blue-600 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              {filteredReceipts.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-slate-600">No matching orders found</p>
                  <p className="text-slate-500 text-sm mt-1">Try a different search term</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS for scanner animations */}
      <style>{`
        @keyframes scanMove {
          0%, 100% { top: 0; opacity: 0.5; }
          50% { top: 100%; opacity: 1; }
        }
        @keyframes scanBeamSweep {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes detectFlash {
          0%, 55%, 100% { opacity: 0; transform: scale(1.04); }
          62%, 88% { opacity: 1; transform: scale(1); }
        }
        @keyframes cornerPulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 1; }
        }
        @keyframes statusBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
      `}</style>
    </div>
  );
};

export default InvoiceScanner;